import { fmtDate, fmtDateTime } from '../../lib/format';
import { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Tag,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Space,
  Tooltip,
  Empty,
  Alert,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  EditOutlined,
  StopOutlined,
  PlayCircleOutlined,
  EyeOutlined,
  ReloadOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useAppSelector } from '@/app/hooks';
import { selectHasPermission } from '@/features/auth/authSlice';
import { DryRunBanner, ComplianceStatusBadge } from '@/components/compliance';
import {
  getRetentionPolicies,
  upsertRetentionPolicy,
  disableRetentionPolicy,
  runDryRun,
  GOVERNANCE_SERVICES,
  type RetentionPolicy,
  type RetentionPolicyInput,
} from '@/services/dataGovernanceService';

const UNIT_OPTIONS: RetentionPolicy['retention_period_unit'][] = ['DAYS', 'MONTHS', 'YEARS'];

const TRIGGER_OPTIONS: RetentionPolicy['trigger_event'][] = [
  'CREATED_AT',
  'TERMINATED_AT',
  'CONTRACT_ENDED_AT',
  'PAYROLL_RUN_LOCKED_AT',
  'DOCUMENT_EXPIRED_AT',
];

const ACTION_OPTIONS: RetentionPolicy['action_after_retention'][] = [
  'REVIEW',
  'ANONYMIZE',
  'DELETE',
  'RETAIN',
];

const humanize = (value: string): string =>
  value
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const shortId = (id: string): string => (id.length > 8 ? `${id.slice(0, 8)}…` : id);

function RetentionPoliciesPage() {
  const canCreate = useAppSelector(selectHasPermission('compliance:retention_policy:create'));
  const canUpdate = useAppSelector(selectHasPermission('compliance:retention_policy:update'));

  const [policies, setPolicies] = useState<RetentionPolicy[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<RetentionPolicy | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<RetentionPolicyInput>();

  const watchAction = Form.useWatch('action_after_retention', form);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getRetentionPolicies();
      setPolicies(data);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to load retention policies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      retention_period_unit: 'DAYS',
      trigger_event: 'CREATED_AT',
      action_after_retention: 'REVIEW',
      legal_hold_overrides: true,
    } as Partial<RetentionPolicyInput> as RetentionPolicyInput);
    setModalOpen(true);
  };

  const openEdit = (policy: RetentionPolicy) => {
    setEditing(policy);
    form.setFieldsValue({
      service_name: policy.service_name,
      record_category: policy.record_category,
      country_code: policy.country_code ?? undefined,
      tenant_id: policy.tenant_id,
      retention_period_value: policy.retention_period_value,
      retention_period_unit: policy.retention_period_unit,
      trigger_event: policy.trigger_event,
      action_after_retention: policy.action_after_retention,
      legal_hold_overrides: policy.legal_hold_overrides,
      is_active: policy.is_active,
    } as unknown as RetentionPolicyInput);
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const input: RetentionPolicyInput = {
        service_name: values.service_name,
        record_category: values.record_category,
        country_code: values.country_code ? values.country_code : null,
        tenant_id: editing?.tenant_id ?? null,
        retention_period_value: values.retention_period_value,
        retention_period_unit: values.retention_period_unit,
        trigger_event: values.trigger_event,
        action_after_retention: values.action_after_retention,
        legal_hold_overrides: values.legal_hold_overrides,
        is_active: editing ? editing.is_active : true,
      };
      await upsertRetentionPolicy(input);
      message.success(editing ? 'Retention policy updated' : 'Retention policy created');
      setModalOpen(false);
      await load();
    } catch (e: any) {
      if (e?.errorFields) return; // validation errors are surfaced inline
      message.error(e?.response?.data?.message || 'Failed to save retention policy');
    } finally {
      setSaving(false);
    }
  };

  const handleDisable = async (policy: RetentionPolicy) => {
    try {
      await disableRetentionPolicy(policy.id);
      message.info('Disable is not yet enforced by the backend');
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to disable policy');
    }
  };

  const handleDryRun = async () => {
    try {
      const res = await runDryRun();
      message.info(res.note);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to start dry-run');
    }
  };

  const columns: ColumnsType<RetentionPolicy> = [
    {
      title: 'Policy',
      dataIndex: 'record_category',
      key: 'policy',
      render: (v: string) => <strong>{v}</strong>,
    },
    {
      title: 'Country',
      dataIndex: 'country_code',
      key: 'country_code',
      render: (v: string | null) => v || '—',
    },
    {
      title: 'Tenant',
      dataIndex: 'tenant_id',
      key: 'tenant_id',
      render: (v: string | null) => (v ? shortId(v) : 'Platform default'),
    },
    {
      title: 'Service',
      dataIndex: 'service_name',
      key: 'service_name',
      render: (v: string) => <Tag>{v}</Tag>,
    },
    {
      title: 'Record category',
      dataIndex: 'record_category',
      key: 'record_category',
    },
    {
      title: 'Retention period',
      key: 'retention_period',
      render: (_: unknown, r) => `${r.retention_period_value} ${r.retention_period_unit}`,
    },
    {
      title: 'Trigger event',
      dataIndex: 'trigger_event',
      key: 'trigger_event',
      render: (v: string) => humanize(v),
    },
    {
      title: 'Action after retention',
      key: 'action_after_retention',
      render: (_: unknown, r) => (
        <Space size={4}>
          <ComplianceStatusBadge kind="retention_action" value={r.action_after_retention} />
          {r.action_after_retention === 'DELETE' && (
            <Tooltip title="Destructive — proposed only, never executed in this release">
              <WarningOutlined style={{ color: 'var(--color-danger)' }} />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: 'Legal hold override',
      dataIndex: 'legal_hold_overrides',
      key: 'legal_hold_overrides',
      render: (v: boolean) =>
        v ? <Tag color="green">Yes</Tag> : <Tag color="default">No</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (v: boolean) =>
        v ? <Tag color="green">Active</Tag> : <Tag color="default">Disabled</Tag>,
    },
    {
      title: 'Last updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (v: string) => (v ? fmtDate(v) : '—'),
    },
    ...(canUpdate
      ? [
          {
            title: 'Actions',
            key: 'actions',
            render: (_: unknown, r: RetentionPolicy) => (
              <Space size={4} wrap>
                <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>
                  Edit
                </Button>
                <Button size="small" icon={<StopOutlined />} onClick={() => handleDisable(r)}>
                  Disable
                </Button>
                <Button size="small" icon={<PlayCircleOutlined />} onClick={handleDryRun}>
                  Run dry-run
                </Button>
                <Tooltip title="Preview listing not available in this release">
                  <Button size="small" icon={<EyeOutlined />} disabled>
                    View matching records
                  </Button>
                </Tooltip>
              </Space>
            ),
          } as ColumnsType<RetentionPolicy>[number],
        ]
      : []),
  ];

  return (
    <Card
      title="Retention Policies"
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
            Refresh
          </Button>
          {canCreate && (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New policy
            </Button>
          )}
        </Space>
      }
    >
      <p style={{ color: 'rgba(0,0,0,0.45)', marginTop: -8 }}>
        Define how long each record category is kept and what happens once its retention period
        elapses. Destructive actions are proposed only and are never executed in this release.
      </p>

      <DryRunBanner />

      <Table<RetentionPolicy>
        rowKey="id"
        columns={columns}
        dataSource={policies}
        loading={loading}
        scroll={{ x: 'max-content' }}
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
        locale={{
          emptyText: <Empty description="No retention policies defined" />,
        }}
        style={{ marginTop: 16 }}
      />

      <Modal
        title={editing ? 'Edit retention policy' : 'New retention policy'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        confirmLoading={saving}
        okText={editing ? 'Save' : 'Create'}
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="service_name"
            label="Service"
            rules={[{ required: true, message: 'Service is required' }]}
          >
            <Select
              placeholder="Select a service"
              options={GOVERNANCE_SERVICES.map((s) => ({ label: s, value: s }))}
            />
          </Form.Item>

          <Form.Item
            name="record_category"
            label="Record category"
            rules={[{ required: true, message: 'Record category is required' }]}
          >
            <Input placeholder="e.g. employee_master_data" />
          </Form.Item>

          <Form.Item name="country_code" label="Country code (optional)">
            <Input placeholder="e.g. TZ" maxLength={2} />
          </Form.Item>

          <Form.Item
            name="retention_period_value"
            label="Retention period value"
            rules={[
              { required: true, message: 'Retention period is required' },
              {
                validator: (_, value) =>
                  typeof value === 'number' && value > 0
                    ? Promise.resolve()
                    : Promise.reject(new Error('Must be a positive number')),
              },
            ]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="retention_period_unit"
            label="Retention period unit"
            rules={[{ required: true }]}
          >
            <Select options={UNIT_OPTIONS.map((u) => ({ label: u, value: u }))} />
          </Form.Item>

          <Form.Item name="trigger_event" label="Trigger event" rules={[{ required: true }]}>
            <Select
              options={TRIGGER_OPTIONS.map((t) => ({ label: humanize(t), value: t }))}
            />
          </Form.Item>

          <Form.Item
            name="action_after_retention"
            label="Action after retention"
            rules={[{ required: true }]}
          >
            <Select options={ACTION_OPTIONS.map((a) => ({ label: humanize(a), value: a }))} />
          </Form.Item>

          {watchAction === 'DELETE' && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="DELETE is a proposed future action only. Destructive execution is disabled in this release."
            />
          )}

          <Form.Item
            name="legal_hold_overrides"
            label="Legal hold overrides retention"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}

export default RetentionPoliciesPage;
